import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { BrandS } from '#src/atoms/typography/index.tsx';

type Props = {
  icon: ReactNode;
  label: ReactNode;
  selected: boolean;
  colorRgb: string;
  viewTransitionName?: string;
  to?: string;
  disabled?: boolean;
  onClick?: () => void;
};

const Pill = styled(ButtonBase)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);
  height: calc(var(--quadruple-unit) + var(--unit));
  min-width: calc(var(--quadruple-unit) + var(--unit));
  padding: 0 var(--double-unit);
  border-radius: 999px;
  color: var(--c-white);
  background: var(--c-black);
  border: 1px solid var(--c-nd-150);
  &:disabled {
    cursor: default;
    color: var(--c-nd-500);
  }

  &.selected {
    background: radial-gradient(
        ellipse 56% 86% at 50% 100%,
        rgba(var(--pill-rgb), 0.6) 0%,
        rgba(var(--pill-rgb), 0) 100%
      )
      no-repeat var(--c-black);
    border-color: rgb(var(--pill-rgb));
    box-shadow: 0 0 16px -6px rgb(var(--pill-rgb));
  }

  view-transition-class: stretch;
`;

const IconWrapper = styled.span`
  display: inline-flex;
`;

export const PillButton = ({
  icon,
  label,
  selected,
  colorRgb,
  viewTransitionName,
  to,
  disabled,
  onClick,
}: Props) => {
  return (
    <Pill
      to={to}
      disabled={disabled}
      className={selected ? 'selected' : undefined}
      onClick={onClick}
      style={
        {
          '--pill-rgb': colorRgb,
          viewTransitionName,
        } as CSSProperties
      }
    >
      <IconWrapper
        style={
          viewTransitionName
            ? { viewTransitionName: `${viewTransitionName}-icon` }
            : undefined
        }
      >
        {icon}
      </IconWrapper>
      {selected ? (
        <BrandS
          color="var(--c-white)"
          style={
            viewTransitionName
              ? { viewTransitionName: `${viewTransitionName}-label` }
              : undefined
          }
        >
          {label}
        </BrandS>
      ) : null}
    </Pill>
  );
};
